// src/components/home/HeroSection.js
import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaPlay, FaTimes } from 'react-icons/fa';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import Button from '../common/Button';
// Import the Modal component
import Modal from '../common/Modal';

const HeroSection = () => {
  // State for video modal
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  // State for background image slider
  const [currentBgImage, setCurrentBgImage] = useState(0);
  
  // Background images array
  const backgroundImages = [
    '/images/hero/hero-bg-1.jpg',
    '/images/hero/hero-bg-2.jpg',
    '/images/hero/hero-bg-3.jpg',
    '/images/hero/hero-bg-4.jpg',
  ];
  
  // YouTube video ID - replace with your actual video ID
  const youtubeVideoId = 'TXH8wiHE0dM';
  
  useEffect(() => {
    // GSAP animation for the hero content
    gsap.fromTo(
      '.hero-content',
      { opacity: 0, y: 50 },
      { opacity: 1, y: 0, duration: 1, ease: 'power3.out' }
    );
    
    // Background image rotation
    const bgInterval = setInterval(() => {
      setCurrentBgImage(prevIndex => (prevIndex + 1) % backgroundImages.length);
    }, 5000);
    
    return () => clearInterval(bgInterval);
  }, [backgroundImages.length]);
  
  // Toggle video modal
  const toggleVideoModal = () => {
    setIsVideoModalOpen(!isVideoModalOpen);
  };
  
  // Simple inline video modal component if the imported Modal has issues
  const VideoModal = ({ isOpen, onClose }) => {
    const iframeRef = useRef(null);
    
    // This useEffect ensures the video keeps playing
    useEffect(() => {
      if (isOpen && iframeRef.current) {
        // Give the iframe a small delay to fully load
        const timer = setTimeout(() => {
          // This can help "kick" the player if it's stopping
          const iframe = iframeRef.current;
          if (iframe) {
            // Force a refresh of the iframe src
            const currentSrc = iframe.src;
            iframe.src = currentSrc;
          }
        }, 500);
        
        return () => clearTimeout(timer);
      }
    }, [isOpen]);
    
    if (!isOpen) return null;
    
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center">
        <div className="fixed inset-0 bg-black bg-opacity-75" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg w-full max-w-3xl mx-4 z-10" onClick={e => e.stopPropagation()}>
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-xl font-bold">Leotech: Skills Against Poverty</h3>
            <button 
              onClick={onClose}
              className="p-1 rounded-full hover:bg-gray-200"
            >
              <FaTimes className="h-5 w-5" />
            </button>
          </div>
          <div className="p-4">
            <div className="aspect-w-16 aspect-h-9">
              <iframe
                ref={iframeRef}
                src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&rel=0&modestbranding=1&enablejsapi=1&mute=0&controls=1`}
                title="Leotech Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
                style={{ aspectRatio: '16/9', height: '400px' }}
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    );
  };
  return (
    <section className="relative min-h-screen pt-16 overflow-hidden flex items-center justify-center">
      {/* Background Images with Overlay - reduced opacity */}
      <div className="absolute inset-0 z-0">
        {backgroundImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 bg-cover bg-center ${
              index === currentBgImage ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              backgroundImage: `url(${image})`,
            }}
          />
        ))}
        {/* Dark overlay with gradient - reduced opacity from 90% to 60% */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-dark to-primary opacity-60" />
      </div>
      
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10 z-10">
        <div className="absolute top-0 left-0 w-full h-full grid grid-cols-10 grid-rows-10">
          {Array.from({ length: 100 }).map((_, index) => (
            <div
              key={index}
              className="border border-white border-opacity-20"
            />
          ))}
        </div>
      </div>
      
      {/* Animated shapes */}
      <motion.div
        className="absolute top-1/4 left-10 w-32 h-32 bg-white rounded-full opacity-10 z-10"
        animate={{
          y: [0, 50, 0],
          x: [0, 20, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      
      <motion.div
        className="absolute bottom-1/4 right-20 w-20 h-20 bg-secondary rounded-full opacity-20 z-10"
        animate={{
          y: [0, -30, 0],
          x: [0, -20, 0],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      
      <motion.div
        className="absolute top-1/3 right-1/4 w-16 h-16 bg-white rounded-full opacity-10 z-10"
        animate={{
          y: [0, 40, 0],
          x: [0, -30, 0],
        }}
        transition={{
          duration: 7,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      
      {/* Center-aligned content container */}
      <div className="container mx-auto px-4 py-20 z-20 flex items-center justify-center relative">
        {/* Hero Content - now centered and with max-width */}
        <div className="hero-content text-center max-w-3xl">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Skills Against Poverty
          </h1>
          <p className="text-xl text-white opacity-90 mb-8 mx-auto">
            Empowering Sierra Leone's youth through marketable digital skills and fostering research and innovation.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4 mb-8">
            <Button 
              variant="secondary" 
              size="lg" 
              to="/programs"
              icon={<FaArrowRight />}
            >
              Explore Programs
            </Button>
            <Button 
              variant="light" 
              size="lg" 
              to="/about"
            >
              Learn More
            </Button>
          </div>
          
          {/* Video Play Button - centered */}
          <div className="flex items-center justify-center cursor-pointer group" onClick={toggleVideoModal}>
            <div className="w-14 h-14 bg-secondary rounded-full flex items-center justify-center shadow-lg mr-4 group-hover:bg-secondary-dark transition-colors">
              <FaPlay className="text-white ml-1" />
            </div>
            <span className="text-white font-medium group-hover:text-secondary transition-colors">
              Watch our video
            </span>
          </div>
        </div>
      </div>
      
      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20">
        <motion.div
          className="flex flex-col items-center text-white text-sm"
          animate={{
            y: [0, 10, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <span className="mb-2">Scroll Down</span>
          <svg 
            className="w-6 h-6"
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M19 14l-7 7m0 0l-7-7m7 7V3" 
            />
          </svg>
        </motion.div>
      </div>
      
      {/* Use the inline VideoModal instead of the imported one */}
      <VideoModal isOpen={isVideoModalOpen} onClose={toggleVideoModal} />
    </section>
  );
};

export default HeroSection;